import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useSessionStore } from '@/stores/SessionStore';

interface EnabledFeatures {
  enabled: string[];
  modules: { key: string; name: string; level: string }[];
}

/**
 * Feature gate for navigation and routes (FR-PLT-05 layers 1-2): a menu entry or route tagged with a
 * feature key is shown only when that feature is on for the company (built, switched on by plan,
 * sign-up answers or admin, and its dependencies on). Role permissions (layer 3) are checked separately.
 *
 * Untagged entries are always shown. While the list is loading, or if it cannot be loaded, everything is
 * shown (fail-open): the server still refuses what is off, and a gate must never hide a working module
 * because of a network blip.
 */
export function useFeatureGate() {
  const { isAuthenticated } = useSessionStore();
  const query = useQuery({
    queryKey: ['features', 'enabled'],
    queryFn: () => api.get<EnabledFeatures>('/features'),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const enabled = query.data?.enabled;
  const isFeatureEnabled = useMemo(() => {
    if (!enabled) return (_code?: string) => true;
    const on = new Set(enabled.map((k) => k.toLowerCase()));
    return (code?: string) => !code || on.has(code.toLowerCase());
  }, [enabled]);

  return { isFeatureEnabled, isLoading: query.isLoading, modules: query.data?.modules ?? [] };
}
